/*
================================================================================
FILE: CommandBar.jsx
Location: src/pages/AIControlRoomAdaptive/components/CommandBar.jsx
================================================================================
*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Calendar, Building2, Megaphone, MessageSquare, Globe } from 'lucide-react';
import styles from '../AIControlRoomAdaptive.module.css';
import { useControlRoomStore } from '../utils/controlRoomStore';
import { useAuth } from '../../../context/AuthContext';

const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'thisMonth', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'allTime', label: 'All Time' }
];

// Dropdown component for filter selects
const FilterDropdown = ({
  icon: Icon,
  value,
  options,
  onChange,
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.filterDropdown} ref={dropdownRef}>
      <button
        className={styles.filterButton}
        onClick={() => setIsOpen(!isOpen)}
      >
        {Icon && <Icon size={14} className={styles.filterIcon} />}
        <span className={styles.filterValue}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown size={14} className={`${styles.filterChevron} ${isOpen ? styles.open : ''}`} />
      </button>

      {isOpen && (
        <div className={styles.filterMenu}>
          {options.map(option => (
            <button
              key={option.value}
              className={`${styles.filterOption} ${option.value === value ? styles.selected : ''}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CommandBar = ({
  tenants = [],
  campaigns = [],
  selectedTenant = 'all',
  selectedCampaign = 'all',
  selectedDateRange = 'last7',
  onTenantChange,
  onCampaignChange,
  onDateRangeChange,
  showTenantFilter = false,
  showWebChannel = true
}) => {
  const { viewState, setViewState, channel, setChannel } = useControlRoomStore();
  // Review accounts and prospect demos show sample data, so the badge must not claim the data is live.
  const { user } = useAuth();
  const isReviewer = user?.is_reviewer === true;
  
  const tenantOptions = [
    { value: 'all', label: 'All Tenants' },
    ...tenants.map(t => ({ value: t.id, label: t.name }))
  ];
  
  const campaignOptions = [
    { value: 'all', label: 'All Campaigns' },
    ...campaigns.map(c => ({ value: c.id, label: c.name }))
  ];
  
  const states = [
    { value: 'default', label: 'Default' },
    { value: 'hot-spike', label: 'Hot Spike' },
    { value: 'high-volume', label: 'High Volume' },
    { value: 'quiet', label: 'Quiet Time' }
  ];
  
  return (
    <div className={styles.commandBar}>

      {/* Channel Toggle — only shown when this tenant has a web-chat campaign
          (gated identically to the left-nav Web Chat item). With SMS the only
          channel, a toggle would be a confusing one-option control, so hide it. */}
      {showWebChannel && (
        <div className={styles.channelRow}>
          <div className={styles.channelToggle}>
            <button
              className={`${styles.channelBtn} ${channel === 'sms' ? styles.channelBtnActiveSms : ''}`}
              onClick={() => setChannel('sms')}
            >
              <MessageSquare size={13} />
              SMS
            </button>
            <button
              className={`${styles.channelBtn} ${channel === 'web' ? styles.channelBtnActiveWeb : ''}`}
              onClick={() => setChannel('web')}
            >
              <Globe size={13} />
              Web
            </button>
          </div>
        </div>
      )}

      {/* Responsive row: filters + state */}
      <div className={styles.commandRow}>
        {/* Left: Filters */}
        <div className={styles.commandLeft}>
          {showTenantFilter && (
            <FilterDropdown
              icon={Building2}
              value={selectedTenant}
              options={tenantOptions}
              onChange={onTenantChange}
              placeholder="All Tenants"
            />
          )}

          <FilterDropdown
            icon={Megaphone}
            value={selectedCampaign}
            options={campaignOptions}
            onChange={onCampaignChange}
            placeholder="All Campaigns"
          />

          <FilterDropdown
            icon={Calendar}
            value={selectedDateRange}
            options={DATE_PRESETS}
            onChange={onDateRangeChange}
            placeholder="Last 7 days"
          />

          <div
            className={styles.liveIndicator}
            style={isReviewer ? { background: '#FEF3C7', borderColor: '#FCD34D', color: '#B45309' } : undefined}
          >
            <span className={styles.pulseDot} style={{ background: isReviewer ? '#F59E0B' : channel === 'web' ? '#6366F1' : '#16A34A' }} />
            {isReviewer ? 'SAMPLE DATA' : 'LIVE'} · {channel === 'web' ? 'Web' : 'SMS'}
          </div>
        </div>

        {/* Right: State Toggles — hidden on mobile (state controlled via mobile tabs) */}
        <div className={styles.stateSelector}>
          {states.map(state => (
            <button
              key={state.value}
              className={`${styles.stateBtn} ${viewState === state.value ? styles.active : ''}`}
              onClick={() => setViewState(state.value)}
            >
              {state.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandBar;